import React, { useState, useEffect } from "react";
import { Modal, Button, Form, Spinner, Alert } from "react-bootstrap";
import { 
  verificarConvenio, 
  generarConvenio, 
  aceptarConvenio,
  getConvenioUrl 
} from "../api/convenios.api.ts";
import Swal from 'sweetalert2';

/**
 * Modal para mostrar y aceptar el convenio de una gira
 * @param {Object} props
 * @param {number} props.aluId - ID del alumno
 * @param {number} props.girId - ID de la gira
 * @param {string} props.alumnoNombre - Nombre completo del alumno
 * @param {boolean} props.show - Controla si el modal está visible
 * @param {Function} props.onAccepted - Callback cuando se acepta el convenio
 * @param {Function} props.onClose - Callback cuando se cierra sin aceptar
 */
export default function ConvenioModal({ 
  aluId, 
  girId, 
  alumnoNombre = "",
  show = false,
  onAccepted,
  onClose 
}) {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [pdfUrl, setPdfUrl] = useState(null);
  const [aceptoCondiciones, setAceptoCondiciones] = useState(false);
  const [procesando, setProcesando] = useState(false);

  /**
   * Verifica si existe convenio y lo genera si es necesario
   */
  const verificarYGenerarConvenio = async () => {
    try {
      setLoading(true);
      setError(null);

      // Verificar si ya existe convenio
      const verificacion = await verificarConvenio(aluId, girId);

      if (verificacion.error) {
        throw new Error(verificacion.msg || "Error al verificar convenio");
      }

      // Si no necesita aceptar (ya lo aceptó), cerrar el modal
      if (!verificacion.necesitaAceptar) {
        if (onAccepted) onAccepted();
        return;
      }

      // Si no existe el PDF, generarlo
      if (!verificacion.convenio || !verificacion.convenio.coa_pdf_path) {
        const generacion = await generarConvenio(aluId, girId);
        
        if (generacion.error) {
          throw new Error(generacion.msg || "Error al generar convenio");
        }
      }

      // Obtener la URL del PDF
      const url = getConvenioUrl(aluId, girId);
      setPdfUrl(url);
      
    } catch (err) {
      console.error("Error en verificarYGenerarConvenio:", err);
      setError(err.message || "Error al cargar el convenio");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (show && aluId && girId) {
      verificarYGenerarConvenio();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [show, aluId, girId]);

  /**
   * Maneja la aceptación del convenio
   */
  const handleAceptar = async () => {
    if (!aceptoCondiciones) {
      Swal.fire({
        icon: "warning",
        title: "Atención",
        text: "Debe aceptar las condiciones del convenio para continuar."
      });
      return;
    }

    try {
      setProcesando(true);
      
      Swal.fire({
        title: "Registrando aceptación...",
        text: "Por favor, espere...",
        allowOutsideClick: false,
        didOpen: () => {
          Swal.showLoading();
        }
      });

      const resultado = await aceptarConvenio(aluId, girId);

      if (resultado.error) {
        throw new Error(resultado.msg || "Error al aceptar convenio");
      }

      Swal.fire({
        icon: "success",
        title: "¡Convenio Aceptado!",
        text: "El convenio ha sido aceptado exitosamente.",
        timer: 2000,
        showConfirmButton: false
      });

      // Llamar al callback de éxito
      if (onAccepted) onAccepted();
      
    } catch (err) {
      console.error("Error al aceptar convenio:", err);
      Swal.fire({
        icon: "error",
        title: "Error",
        text: err.message || "No se pudo registrar la aceptación del convenio."
      });
    } finally {
      setProcesando(false);
    }
  };

  /**
   * Maneja el cierre del modal
   */
  const handleClose = () => {
    if (!procesando && onClose) {
      onClose();
    }
  };

  return (
    <Modal 
      show={show} 
      onHide={handleClose}
      size="xl"
      backdrop="static"
      keyboard={!procesando}
      centered
    >
      <Modal.Header closeButton={!procesando}>
        <Modal.Title>
          Convenio de Gira - {alumnoNombre}
        </Modal.Title>
      </Modal.Header>

      <Modal.Body style={{ minHeight: "500px" }}>
        {loading && (
          <div className="text-center py-5">
            <Spinner animation="border" variant="primary" />
            <p className="mt-3">Cargando convenio...</p>
          </div>
        )}

        {error && (
          <Alert variant="danger">
            <Alert.Heading>Error al cargar el convenio</Alert.Heading>
            <p>{error}</p>
            <hr />
            <div className="d-flex justify-content-end">
              <Button 
                variant="outline-danger" 
                onClick={verificarYGenerarConvenio}
              >
                Reintentar
              </Button>
            </div>
          </Alert>
        )}

        {!loading && !error && pdfUrl && (
          <div>
            {/* Visor de PDF */}
            <div 
              style={{ 
                border: "1px solid #dee2e6", 
                borderRadius: "5px",
                overflow: "hidden",
                marginBottom: "20px"
              }}
            >
              <iframe
                src={pdfUrl}
                style={{
                  width: "100%",
                  height: "600px",
                  border: "none"
                }}
                title="Convenio PDF"
              />
            </div>

            {/* Checkbox de aceptación */}
            <Form.Group className="mb-3">
              <Form.Check
                type="checkbox"
                id="aceptoCondiciones"
                label={
                  <span>
                    He leído y acepto las condiciones del convenio presentado. 
                    Confirmo que la información es correcta y me comprometo a 
                    cumplir con lo establecido.
                  </span>
                }
                checked={aceptoCondiciones}
                onChange={(e) => setAceptoCondiciones(e.target.checked)}
                disabled={procesando}
              />
            </Form.Group>

            {/* Información adicional */}
            <Alert variant="info" className="mb-0">
              <small>
                <strong>Importante:</strong> Una vez aceptado el convenio, 
                se registrará su aceptación con fecha y hora. Puede descargar 
                una copia del convenio en cualquier momento desde su perfil.
              </small>
            </Alert>
          </div>
        )}
      </Modal.Body>

      <Modal.Footer>
        <Button 
          variant="secondary" 
          onClick={handleClose}
          disabled={procesando}
        >
          Cancelar
        </Button>
        
        {pdfUrl && (
          <Button
            variant="primary"
            href={pdfUrl}
            target="_blank"
            download={`convenio_${alumnoNombre.replace(/\s+/g, '_')}.pdf`}
            disabled={procesando}
          >
            <i className="material-icons">download</i> Descargar PDF
          </Button>
        )}
        
        <Button
          variant="success"
          onClick={handleAceptar}
          disabled={procesando || loading || !aceptoCondiciones}
        >
          {procesando ? (
            <>
              <Spinner
                as="span"
                animation="border"
                size="sm"
                role="status"
                aria-hidden="true"
                className="me-2"
              />
              Procesando...
            </>
          ) : (
            <>
              <i className="material-icons">check_circle</i> Aceptar Convenio
            </>
          )}
        </Button>
      </Modal.Footer>
    </Modal>
  );
}
